import React, { useState } from "react";
import "./AuthForm.css";
import { motion } from "framer-motion";
import { login, register, parseIdentifier, MIN_PASSWORD_LENGTH } from "../auth/session";

export default function AuthForm({ onLogin }) {
  const [isActive, setIsActive] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 760 : false);
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [regUsername, setRegUsername] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regName, setRegName] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirm, setRegConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [showRegConfirm, setShowRegConfirm] = useState(false);
  const [loginUsernameErr, setLoginUsernameErr] = useState("");
  const [loginPasswordErr, setLoginPasswordErr] = useState("");
  const [regUsernameErr, setRegUsernameErr] = useState("");
  const [regPasswordErr, setRegPasswordErr] = useState("");
  const [regConfirmErr, setRegConfirmErr] = useState("");

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 760);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    setStatus("");
    let okLogin = true;
    // Only check "not empty" here: older accounts may have shorter passwords than new ones are allowed to.
    if (!loginUsername.trim()) { setLoginUsernameErr(isMobile ? "Email is required" : "Username is required"); okLogin = false; } else setLoginUsernameErr("");
    if (!loginPassword) { setLoginPasswordErr("Password is required"); okLogin = false; } else setLoginPasswordErr("");
    if (!okLogin) return;
    try {
      setLoading(true);
      const result = await login(loginUsername, loginPassword);
      setStatus(result.message);
      if (result.ok && onLogin) onLogin(result.user);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (loading) return;
    setStatus("");
    let okReg = true;
    const identifier = isMobile ? regEmail : regUsername;
    if (isMobile && (!regName || regName.trim().length < 2)) {
      okReg = false;
      setRegUsernameErr("Name is required");
    } else if (!parseIdentifier(identifier)) {
      okReg = false;
      setRegUsernameErr(isMobile ? "Valid email is required" : "Enter a valid email or 10-digit mobile number");
    } else {
      setRegUsernameErr("");
    }
    if (!regPassword || regPassword.length < MIN_PASSWORD_LENGTH) { setRegPasswordErr(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`); okReg = false; } else setRegPasswordErr("");
    if (regPassword !== regConfirm) { setRegConfirmErr("Passwords do not match"); okReg = false; } else setRegConfirmErr("");
    if (!okReg) return;
    try {
      setLoading(true);
      const result = await register({ identifier, password: regPassword });
      setStatus(result.message);
      if (result.ok) {
        if (result.loggedIn && onLogin) onLogin(result.user);
        else setIsActive(false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`container ${isActive ? "active" : ""}`}>
      <div className="mobile-toggle">
        <button className={`tab ${!isActive ? 'active' : ''}`} onClick={() => setIsActive(false)}>Login</button>
        <button className={`tab ${isActive ? 'active' : ''}`} onClick={() => setIsActive(true)}>Register</button>
      </div>
      <div className="form-container login-container">
        <form onSubmit={handleLogin}>
          {isMobile && (
            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
              <img src={process.env.PUBLIC_URL + '/mileedge.png'} alt="MileEdge" style={{ height: '64px', width: 'auto', objectFit: 'contain' }} />
            </div>
          )}
          {isMobile && <p>Sign in to continue</p>}
          <div className="input-row">
            <input type="text" autoComplete="username" placeholder={isMobile ? "Email" : "Email or mobile number"} value={loginUsername} onChange={(e) => setLoginUsername(e.target.value)} />
          </div>
          {loginUsernameErr && <div className="error">{loginUsernameErr}</div>}
          <div className="input-row">
            <input type={showLoginPwd ? "text" : "password"} autoComplete="current-password" placeholder="Password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} />
            <button type="button" className="toggle-pwd" aria-label="Toggle password" onClick={() => setShowLoginPwd(v => !v)}>
              {showLoginPwd ? "Hide" : "Show"}
            </button>
          </div>
          {loginPasswordErr && <div className="error">{loginPasswordErr}</div>}
          <br />
          <button className="primary" disabled={loading}>Login</button>
          {status && !isActive && <div className="status">{status}</div>}
          {isMobile && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
              <span>Don’t have an account?</span>
              <button type="button" className="link" onClick={() => setIsActive(true)} style={{ background: 'transparent', border: 'none', color: '#185050', fontWeight: 600, cursor: 'pointer', padding: '4px 8px', borderRadius: 8 }}>Register</button>
            </div>
          )}
        </form>
      </div>

      <div className="form-container register-container">
        <form onSubmit={handleRegister}>
          {isMobile && (
            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
              <img src={process.env.PUBLIC_URL + '/mileedge.png'} alt="MileEdge" style={{ height: '64px', width: 'auto', objectFit: 'contain' }} />
            </div>
          )}
          {isMobile && <p>Create your account</p>}
          {isMobile ? (
            <>
              <div className="input-row">
                <input type="text" placeholder="Name" value={regName} onChange={(e) => setRegName(e.target.value)} />
              </div>
              <div className="input-row">
                <input type="text" autoComplete="email" placeholder="Email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} />
              </div>
            </>
          ) : (
            <div className="input-row">
              <input type="text" autoComplete="username" placeholder="Email or mobile number" value={regUsername} onChange={(e) => setRegUsername(e.target.value)} />
            </div>
          )}
          {regUsernameErr && <div className="error">{regUsernameErr}</div>}
          <div className="input-row">
            <input type={showRegPwd ? "text" : "password"} autoComplete="new-password" placeholder="Password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} />
            <button type="button" className="toggle-pwd" aria-label="Toggle password" onClick={() => setShowRegPwd(v => !v)}>
              {showRegPwd ? "Hide" : "Show"}
            </button>
          </div>
          {regPasswordErr && <div className="error">{regPasswordErr}</div>}
          <div className="input-row">
            <input type={showRegConfirm ? "text" : "password"} autoComplete="new-password" placeholder="Confirm Password" value={regConfirm} onChange={(e) => setRegConfirm(e.target.value)} />
            <button type="button" className="toggle-pwd" aria-label="Toggle password" onClick={() => setShowRegConfirm(v => !v)}>
              {showRegConfirm ? "Hide" : "Show"}
            </button>
          </div>
          {regConfirmErr && <div className="error">{regConfirmErr}</div>}
          <br />
          <button className="primary" disabled={loading}>Register</button>
          {status && isActive && <div className="status">{status}</div>}
          {isMobile && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
              <span>Already have an account?</span>
              <button type="button" className="link" onClick={() => setIsActive(false)} style={{ background: 'transparent', border: 'none', color: '#185050', fontWeight: 600, cursor: 'pointer', padding: '4px 8px', borderRadius: 8 }}>Login</button>
            </div>
          )}
        </form>
      </div>

      <div className="overlay-container">
        <div className="overlay">
          <motion.div className="overlay-panel overlay-left"
            initial={false}
            animate={{ opacity: isActive ? 1 : 0.95, y: isActive ? 0 : 6, scale: isActive ? 1 : 0.995 }}
            transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}>
            <img src={process.env.PUBLIC_URL + '/mileedge.png'} alt="MileEdge" className="overlay-logo" />
            <h2>Welcome Back!</h2>
            <p>Already have an account?</p>
            <button className="ghost" onClick={() => setIsActive(false)}>
              Login
            </button>
          </motion.div>

          <motion.div className="overlay-panel overlay-right"
            initial={false}
            animate={{ opacity: isActive ? 0.95 : 1, y: isActive ? 6 : 0, scale: isActive ? 0.995 : 1 }}
            transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}>
            <img src={process.env.PUBLIC_URL + '/mileedge.png'} alt="MileEdge" className="overlay-logo" />
            <h2>Hello, Welcome!</h2>
            <p>Don't have an account?</p>
            <button className="ghost" onClick={() => setIsActive(true)}>
              Register
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
